import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Bell, CheckCircle, Filter, Globe, Shield, Users, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import SEOHead from "@/components/SEOHead";
import LanguageSelector from "@/components/LanguageSelector";

export default function AlertBandiTed() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="Alert Bandi TED"
        description="Ricevi alert sui bandi europei pubblicati su TED. Monitora nuovi bandi UE per CPV, paese, tipo di procedura e valore con TED Monitor."
        path="/it/alert-bandi-ted"
        alternates={[
          { lang: "it", href: "https://tedmonitor.eu/it/alert-bandi-ted" },
          { lang: "en", href: "https://tedmonitor.eu/ted-procurement-alerts" },
          { lang: "x-default", href: "https://tedmonitor.eu/ted-procurement-alerts" },
        ]}
      />

      {/* Header */}
      <header className="border-b bg-white sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo-mark.png" alt="TED Monitor" className="h-8 w-8" />
            <span className="font-semibold text-lg text-primary">TED Monitor</span>
          </Link>
          <div className="flex items-center gap-4">
            <LanguageSelector />
            <Button asChild>
              <Link to="/auth">Inizia ora</Link>
            </Button>
          </div>
        </div>
      </header>

      <main>
        {/* Hero Section */}
        <section className="py-8 md:py-12 bg-gradient-to-b from-primary/5 to-background">
          <div className="container mx-auto px-4 max-w-4xl">
            <h1 className="text-3xl md:text-4xl font-bold text-primary mb-4 leading-tight">
              Alert bandi TED per appalti pubblici europei
            </h1>

            <p className="text-base md:text-lg text-muted-foreground leading-relaxed mb-3">
              Negli appalti pubblici europei, la velocità conta. Con TED Monitor puoi ricevere alert automatici per
              nuove opportunità pubblicate su TED (Tenders Electronic Daily), filtrate secondo i tuoi criteri.
            </p>

            <p className="text-base font-medium text-foreground">
              Nessun controllo manuale. Nessuna opportunità persa.
            </p>

            {/* Internal links block (SEO + crawling) */}
            <div className="mt-6 flex flex-wrap gap-2">
              <Link
                to="/it/monitoraggio-bandi-ue"
                className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/15"
              >
                Monitoraggio bandi UE
              </Link>
              <Link
                to="/it/software-appalti-pubblici-europei"
                className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/15"
              >
                Software appalti pubblici europei
              </Link>
              <Link
                to="/it/accesso-multilingue"
                className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/15"
              >
                Accesso multilingue (24 lingue UE)
              </Link>
              <Link
                to="/it/conformita-sicurezza"
                className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/15"
              >
                Conformità e sicurezza
              </Link>
              <Link
                to="/it/prezzi"
                className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/15"
              >
                Prezzi
              </Link>
            </div>
          </div>
        </section>

        {/* Cosa sono gli alert TED */}
        <section className="py-6 md:py-8">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-start gap-3 mb-4">
              <Bell className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">Cosa sono gli alert bandi TED</h2>
            </div>

            <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
              Gli alert bandi TED sono notifiche automatiche che ti informano quando nuovi appalti pubblici vengono
              pubblicati nel database ufficiale UE. Gli alert ti aiutano ad agire rapidamente e a mantenere la tua
              pipeline di gare strutturata.
            </p>

            <p className="text-muted-foreground mb-3 text-sm">Gli alert ti aiutano a:</p>
            <ul className="space-y-1.5 text-muted-foreground text-sm">
              {[
                "Catturare nuove opportunità appena vengono pubblicate",
                "Reagire più velocemente e preparare le offerte in anticipo",
                "Organizzare il lavoro commerciale e di gara con un workflow ripetibile",
              ].map((item) => (
                <li key={item} className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Dati ufficiali da TED */}
        <section className="py-6 md:py-8 bg-muted/30">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-start gap-3 mb-4">
              <Shield className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">
                Dati ufficiali da Tenders Electronic Daily (TED)
              </h2>
            </div>

            <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
              Tutti gli alert in TED Monitor sono basati su dati ufficiali pubblicati tramite{" "}
              <a
                href="https://ted.europa.eu"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                Tenders Electronic Daily (TED)
              </a>
              .
            </p>

            <p className="text-muted-foreground mb-3 text-sm">Questo garantisce:</p>
            <ul className="space-y-1.5 text-muted-foreground text-sm mb-4">
              {["Affidabilità dei dati", "Informazioni complete sugli appalti", "Copertura coerente a livello UE"].map(
                (item) => (
                  <li key={item} className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                    {item}
                  </li>
                ),
              )}
            </ul>

            <p className="text-muted-foreground leading-relaxed text-sm">
              Non ti affidi a feed parziali o fonti non autoritative.
            </p>

            <p className="mt-4">
              <Link to="/data-source" className="text-primary hover:underline font-medium text-sm">
                Scopri di più sulla fonte dati →
              </Link>
            </p>
          </div>
        </section>

        {/* Personalizza gli alert */}
        <section className="py-6 md:py-8">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-start gap-3 mb-4">
              <Filter className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">Personalizza i tuoi alert sui bandi</h2>
            </div>

            <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
              Ogni organizzazione ha obiettivi di procurement diversi. TED Monitor ti permette di personalizzare gli
              alert in base al tuo ambito di monitoraggio, così ricevi solo bandi rilevanti.
            </p>

            <p className="text-muted-foreground mb-3 text-sm">Puoi creare alert basati su:</p>
            <div className="flex flex-wrap gap-2 mb-4">
              {["Codici CPV", "Paesi UE", "Tipo procedura", "Valore stimato", "Settore industriale"].map((tag) => (
                <span key={tag} className="px-2 py-0.5 bg-primary/10 text-primary rounded-full text-xs font-medium">
                  {tag}
                </span>
              ))}
            </div>

            <p className="text-muted-foreground leading-relaxed text-sm">
              Questo riduce il rumore e mantiene il tuo monitoraggio preciso.
            </p>

            <p className="mt-4">
              <Link to="/it/monitoraggio-bandi-ue" className="text-primary hover:underline font-medium text-sm">
                Esplora il monitoraggio bandi UE →
              </Link>
            </p>
          </div>
        </section>

        {/* Notifiche in tempo reale */}
        <section className="py-6 md:py-8 bg-muted/30">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-start gap-3 mb-4">
              <Zap className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">
                Notifiche in tempo reale per nuovi bandi UE
              </h2>
            </div>

            <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
              Gli alert vengono generati in linea con le pubblicazioni ufficiali TED, così puoi identificare opportunità
              in anticipo e pianificare la tua attività di gara.
            </p>

            <p className="text-muted-foreground mb-3 text-sm">Benefici operativi:</p>
            <ul className="space-y-1.5 text-muted-foreground text-sm mb-4">
              {[
                "Nessun ritardo informativo nel tuo flusso di monitoraggio",
                "Migliore pianificazione e prioritizzazione delle offerte",
                "Maggiore possibilità di partecipare a bandi rilevanti",
              ].map((item) => (
                <li key={item} className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>

            <p className="text-muted-foreground leading-relaxed text-sm">
              Questo è particolarmente prezioso nelle categorie di appalto competitive.
            </p>
          </div>
        </section>

        {/* Chi ne beneficia */}
        <section className="py-6 md:py-8">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-start gap-3 mb-4">
              <Users className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">Chi beneficia degli alert bandi TED</h2>
            </div>

            <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
              Gli alert bandi TED sono utilizzati da organizzazioni che necessitano di una pipeline di gare strutturata
              in tutta Europa.
            </p>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
              {[
                "Aziende che partecipano a gare UE",
                "Consulenti e studi specializzati",
                "Operatori internazionali",
                "PMI che entrano nel mercato UE",
              ].map((item) => (
                <div key={item} className="p-2 bg-muted/50 rounded-lg border text-center">
                  <p className="font-medium text-foreground text-xs">{item}</p>
                </div>
              ))}
            </div>

            <p className="text-muted-foreground leading-relaxed text-sm">
              Gli alert rendono il mercato più accessibile e riducono significativamente i tempi di discovery degli
              appalti.
            </p>
          </div>
        </section>

        {/* Perché usare TED Monitor */}
        <section className="py-6 md:py-8 bg-muted/30">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-start gap-3 mb-4">
              <Globe className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">
                Perché usare TED Monitor per gli alert
              </h2>
            </div>

            <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
              Rispetto al monitoraggio manuale o a soluzioni frammentate, TED Monitor offre un workflow centralizzato
              per costruire e mantenere la tua pipeline di gare.
            </p>

            <ul className="space-y-1.5 text-muted-foreground text-sm mb-4">
              {[
                "Configurazione alert semplice",
                "Criteri di filtraggio avanzati",
                "Dati bandi strutturati e comparabili",
                "Visibilità centralizzata sul mercato UE",
              ].map((item) => (
                <li key={item} className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>

            <p className="text-muted-foreground leading-relaxed text-sm">
              Scopri di più su fiducia e garanzie della piattaforma:
            </p>

            <div className="mt-3 flex flex-wrap gap-3 text-sm">
              <Link to="/it/accesso-multilingue" className="text-primary hover:underline font-medium">
                Accesso multilingue →
              </Link>
              <Link to="/it/conformita-sicurezza" className="text-primary hover:underline font-medium">
                Conformità e sicurezza →
              </Link>
              <Link to="/it/software-appalti-pubblici-europei" className="text-primary hover:underline font-medium">
                Panoramica software procurement →
              </Link>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-8 md:py-12 bg-primary text-primary-foreground">
          <div className="container mx-auto px-4 max-w-4xl text-center">
            <h2 className="text-xl md:text-2xl font-semibold mb-4">Inizia a ricevere alert bandi TED</h2>
            <p className="text-base mb-6 opacity-90">
              Se vuoi scoprire opportunità di gara UE sistematicamente, gli alert sui bandi sono essenziali.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button size="default" variant="secondary" asChild>
                <Link to="/it/prezzi" className="gap-2">
                  Vedi i prezzi
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
              <Button
                size="default"
                variant="outline"
                className="bg-transparent border-primary-foreground text-primary-foreground hover:bg-primary-foreground hover:text-primary"
                asChild
              >
                <Link to="/auth">Inizia ora</Link>
              </Button>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t py-8 bg-background">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <img src="/logo-mark.png" alt="TED Monitor" className="h-6 w-6" />
              <span className="text-sm text-muted-foreground">© {new Date().getFullYear()} TED Monitor</span>
            </div>
            <nav className="flex flex-wrap gap-6 text-sm">
              <Link to="/it/prezzi" className="text-muted-foreground hover:text-foreground">
                Prezzi
              </Link>
              <Link to="/it/monitoraggio-bandi-ue" className="text-muted-foreground hover:text-foreground">
                Monitoraggio bandi UE
              </Link>
              <Link to="/data-source" className="text-muted-foreground hover:text-foreground">
                Fonte dati
              </Link>
              <Link to="/privacy-policy" className="text-muted-foreground hover:text-foreground">
                Privacy Policy
              </Link>
              <Link to="/terms-conditions" className="text-muted-foreground hover:text-foreground">
                Termini
              </Link>
            </nav>
          </div>
        </div>
      </footer>
    </div>
  );
}
